import { createFileRoute, useLocation } from "@tanstack/react-router";
import { Settings2 } from "lucide-react";
import { OrganizationSettingsPage } from "@/components/settings/organization/organization-settings-page";
import { PageColumn, PageShell } from "@/components/ui/page-shell";
import { useOrganizations } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
	type OrganizationSettingsSection,
	organizationSettingsSection,
	settingsDestinationWidth,
} from "@/lib/workspace-navigation";

export const Route = createFileRoute("/$owner/settings/organization")({
	component: OrganizationSettingsRoute,
	validateSearch: (
		search: Record<string, unknown>,
	): {
		connector?: string;
		connection?: number;
		agent?: string;
		section?: OrganizationSettingsSection;
	} => ({
		section: ["general", "policies", "governance"].includes(
			String(search.section),
		)
			? (search.section as OrganizationSettingsSection)
			: undefined,
		connector:
			typeof search.connector === "string" ? search.connector : undefined,
		connection:
			Number.isSafeInteger(Number(search.connection)) &&
			Number(search.connection) > 0
				? Number(search.connection)
				: undefined,
		agent: typeof search.agent === "string" ? search.agent : undefined,
	}),
});

function OrganizationSettingsRoute() {
	const { owner } = Route.useParams();
	const search = Route.useSearch();
	const { hash } = useLocation();
	const section = organizationSettingsSection(search, hash);
	const title = {
		general: "General",
		policies: "Policies",
		governance: "Governance",
	}[section];
	const { data: allOrgs } = useOrganizations();
	const orgName = (allOrgs ?? []).find(
		(o) => o.slug === owner && o.is_member,
	)?.name;

	return (
		<PageShell className="pt-2.5 pb-8">
			<PageColumn
				className={cn(
					"flex flex-1 flex-col gap-6",
					settingsDestinationWidth(section),
				)}
			>
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<Settings2 className="h-7 w-7" />
						<h1 className="text-3xl font-semibold leading-tight">{title}</h1>
					</div>
					{orgName ? (
						<p className="text-base font-medium text-foreground">{orgName}</p>
					) : null}
				</div>
				<OrganizationSettingsPage owner={owner} {...search} section={section} />
			</PageColumn>
		</PageShell>
	);
}
